import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight } from 'lucide-react';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Application, ApplicationStatus } from '@core/types';
import { rangeStartMs, type DashboardDateRange } from './DashboardDateRangePicker';

type StageDef = {
  key: string;
  label: string;
  reachedOrBeyond: ApplicationStatus[];
  /** Saturated colour for the solid bar (left portion of the section). */
  bar: string;
  /** Light tint of the same colour family that fills the remainder of the section. */
  light: string;
  /** Pill classes for the trend chip in the header row. */
  chip: string;
};

const STAGES: StageDef[] = [
  {
    key: 'submitted',
    label: 'Submitted',
    reachedOrBeyond: [
      'submitted',
      'viewed_by_client',
      'shortlisted',
      'interview_1_scheduled',
      'interview_1_conducted',
      'interview_2_scheduled',
      'interview_2_conducted',
      'offer_extended',
      'offer_accepted',
      'offer_rejected',
      'placed',
    ],
    bar: 'bg-sky-500',
    light: 'bg-sky-500/18',
    chip: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  },
  {
    key: 'shortlisted',
    label: 'Shortlisted',
    reachedOrBeyond: [
      'shortlisted',
      'interview_1_scheduled',
      'interview_1_conducted',
      'interview_2_scheduled',
      'interview_2_conducted',
      'offer_extended',
      'offer_accepted',
      'offer_rejected',
      'placed',
    ],
    bar: 'bg-emerald-500',
    light: 'bg-emerald-500/18',
    chip: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  },
  {
    key: 'interview',
    label: 'Interview',
    reachedOrBeyond: [
      'interview_1_scheduled',
      'interview_1_conducted',
      'interview_2_scheduled',
      'interview_2_conducted',
      'offer_extended',
      'offer_accepted',
      'offer_rejected',
      'placed',
    ],
    bar: 'bg-cyan-500',
    light: 'bg-cyan-500/18',
    chip: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  },
  {
    key: 'offer',
    label: 'Offer',
    reachedOrBeyond: ['offer_extended', 'offer_accepted', 'offer_rejected', 'placed'],
    bar: 'bg-fuchsia-500',
    light: 'bg-fuchsia-500/18',
    chip: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
  },
  {
    key: 'placed',
    label: 'Placed',
    reachedOrBeyond: ['placed'],
    bar: 'bg-pink-500',
    light: 'bg-pink-500/18',
    chip: 'bg-pink-500/15 text-pink-700 dark:text-pink-300',
  },
];

const CHART_H = 260;
/** Fraction of each section taken by the solid bar; the rest is the light fill of the same colour. */
const SOLID_FRACTION = 0.3;

function isWithin(tsIso: string | null | undefined, cutoff: number | null): boolean {
  if (cutoff === null) return true;
  if (!tsIso) return false;
  const t = Date.parse(tsIso);
  if (!Number.isFinite(t)) return false;
  return t >= cutoff;
}

export function DashboardFunnelCard({
  orgId,
  range,
}: {
  orgId: string | null;
  range: DashboardDateRange;
}) {
  const cutoff = rangeStartMs(range);

  const q = useQuery<Application[]>({
    queryKey: ['dashboardAnalytics', 'applications-all', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      if (!orgId) return [];
      return applicationService.list(orgId, { limit: 500 });
    },
    staleTime: 60_000,
  });

  const { reached, total, maxReached } = useMemo(() => {
    const apps = (q.data ?? []).filter((a) =>
      isWithin(a.created_at as unknown as string, cutoff),
    );
    const reached = STAGES.map(
      (s) => apps.filter((a) => s.reachedOrBeyond.includes(a.status)).length,
    );
    return {
      reached,
      total: apps.length,
      maxReached: Math.max(1, ...reached),
    };
  }, [q.data, cutoff]);

  return (
    <section className="rounded-[10px] border vscode-border vscode-bg-secondary p-4 sm:p-5">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold vscode-text-primary">Pipeline Overview</h2>
          <p className="text-[11px] vscode-text-tertiary">
            {q.isError
              ? 'Data unavailable.'
              : q.isLoading
                ? 'Loading…'
                : `${total} applications in range · cumulative stage counts`}
          </p>
        </div>
      </header>

      {q.isError ? (
        <div className="flex items-center justify-between gap-2 text-xs vscode-text-tertiary">
          <span>Could not load funnel.</span>
          <button
            type="button"
            onClick={() => void q.refetch()}
            className="rounded border vscode-border px-2 py-0.5 text-[11px] hover:vscode-bg-hover hover:vscode-text-primary"
          >
            Retry
          </button>
        </div>
      ) : q.isLoading ? (
        <div className="space-y-3" aria-busy="true">
          <div className="grid grid-cols-5 gap-4">
            {STAGES.map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded vscode-bg-tertiary" />
            ))}
          </div>
          <div className="grid grid-cols-5 gap-4">
            {STAGES.map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-[10px] vscode-bg-tertiary"
                style={{ height: CHART_H }}
              />
            ))}
          </div>
        </div>
      ) : total === 0 ? (
        <p className="text-sm vscode-text-tertiary">No applications in this range.</p>
      ) : (
        <div className="space-y-3">
          {/* Header row: stage label / count / trend pill (mirrors Mobbin Pin). */}
          <div className="grid grid-cols-5 gap-4">
            {STAGES.map((s, i) => {
              const count = reached[i];
              const prev = i === 0 ? null : reached[i - 1];
              const trendPct =
                prev !== null && prev > 0 ? Math.round((count / prev) * 100) : null;
              return (
                <div key={s.key} className="min-w-0">
                  <p className="truncate text-[11px] vscode-text-tertiary">{s.label}</p>
                  <p className="mt-0.5 text-xl font-semibold tabular-nums vscode-text-primary">
                    {count}
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    {trendPct === null ? (
                      <span className="text-[10px] font-medium vscode-text-tertiary tabular-nums">
                        —
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.chip}`}
                      >
                        <ArrowUpRight className="h-2.5 w-2.5" aria-hidden />
                        {trendPct}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/*
            Cascading sections (mirrors Mobbin Pin reference):
            - 5 equal-width columns with gap-4 between → horizontal separation.
            - Each column has a SINGLE composite bar, top-anchored, height = reached[i] / maxReached × CHART_H.
            - Inside the composite bar: LEFT portion (~30%) is the saturated solid colour, RIGHT portion is
              a light tint of the SAME colour family (/18). Heights descend → cascade silhouette.
            - A conversion pill (reached[i+1] / reached[i]) floats on the light portion (mirrors 106% / 86% / 46%).
          */}
          <div
            className="grid grid-cols-5 gap-4"
            style={{ height: CHART_H }}
            role="img"
            aria-label="Pipeline overview cascade"
          >
            {STAGES.map((s, i) => {
              const count = reached[i];
              const nextCount = i < STAGES.length - 1 ? reached[i + 1] : null;
              const barH = Math.max(6, Math.round((count / maxReached) * CHART_H));
              const convPct =
                nextCount !== null && count > 0
                  ? Math.round((nextCount / count) * 100)
                  : null;
              const isLast = i === STAGES.length - 1;
              return (
                <div key={s.key} className="relative h-full">
                  {/* Composite bar, top-anchored. */}
                  <div
                    className="absolute left-0 right-0 top-0 flex overflow-hidden rounded-md shadow-inner"
                    style={{ height: barH }}
                  >
                    {/* Solid left portion. */}
                    <div
                      className={s.bar}
                      style={{ width: `${SOLID_FRACTION * 100}%` }}
                      title={`${s.label}: ${count}`}
                    />
                    {/* Light right portion (same colour family). */}
                    <div className={`${s.light} relative flex-1`}>
                      {!isLast && convPct !== null && barH >= 28 ? (
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                          <span className="rounded-full border vscode-border vscode-bg-elevated px-2 py-0.5 text-[10px] font-semibold tabular-nums vscode-text-primary">
                            {convPct}%
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Count chip, on the bottom edge of the bar (or below it when the bar is too short). */}
                  {count > 0 && barH >= 24 ? (
                    <div
                      className="absolute"
                      style={{
                        top: Math.max(4, barH - 22),
                        left: `${(SOLID_FRACTION * 100) / 2}%`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      <span className="rounded bg-black/25 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums backdrop-blur-sm">
                        {count}
                      </span>
                    </div>
                  ) : count >= 0 ? (
                    <div
                      className="absolute left-1/2 -translate-x-1/2"
                      style={{ top: barH + 6 }}
                    >
                      <span className="rounded border vscode-border vscode-bg-elevated px-1.5 py-0.5 text-[10px] font-semibold tabular-nums vscode-text-primary">
                        {count}
                      </span>
                    </div>
                  ) : null}

                  {/* Conversion pill fallback when the bar is too short to host it internally. */}
                  {!isLast && convPct !== null && barH < 28 ? (
                    <div
                      className="absolute left-1/2 -translate-x-1/2"
                      style={{ top: Math.min(CHART_H - 20, barH + 26) }}
                    >
                      <span className="rounded-full border vscode-border vscode-bg-elevated px-2 py-0.5 text-[10px] font-semibold tabular-nums vscode-text-primary">
                        {convPct}%
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Stage axis labels beneath the cascade. */}
          <div className="grid grid-cols-5 gap-4 pt-1">
            {STAGES.map((s) => (
              <p key={s.key} className="truncate text-center text-[10px] vscode-text-tertiary">
                {s.label}
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
